/* eslint-disable */
import { Timestamp } from "../../../../google/protobuf/timestamp";

export const protobufPackage = "od_protos.web.data.sell_direct";

/**
 * Response for home condition input
 * Next ID: 6
 */
export interface HomeConditionResponse {
  homeConditions: HomeCondition[];
  /** end of flow survey answer, "" if user skipped the question, example: "Very easy", "Somewhat difficult" */
  flowDifficulty: string;
  /** submitted by which customer */
  humanId: string;
  /** when user submitted the whole flow */
  submittedAt?:
    | Date
    | undefined;
  /** when user skipped the whole flow */
  skippedAt?:
    | Date
    | undefined;
  /** if the address has mls_photos for the customer to look at */
  hasMlsPhotos: boolean;
}

/**
 * Proxy image for user to choose for the home condition
 * Next ID: 6
 */
export interface ProxyImage {
  /** identifier */
  id: string;
  /** example: It needs some work */
  title: string;
  /** example: It’s a fixer-upper, needs a bit of attention. */
  description: string;
  /** example: https://imgdrop.opendoor.com/xxx */
  imageUrl: string;
  /** rating for the image */
  score: number;
}

/**
 * Data for each home condition type (kitchen, bathroom, etc.)
 * Next ID: 5
 */
export interface HomeCondition {
  /** example: Kitchen, Main bathroom */
  title: string;
  /** proxy image options for user to choose from */
  proxyImages: ProxyImage[];
  /** images shown in "see examples" modal */
  exampleProxyImages: ProxyImage[];
  /** "" if not selected */
  selectedProxyImageId: string;
}

/**
 * Response for price adjustment query
 * Next ID: 6
 */
export interface PriceAdjustmentResponse {
  /** ID of the offer it was submitted for (if any) */
  submittedByOffer: string;
  /** Basis point increase (if any) to the headline price */
  bpsIncrease: number;
  /** Whether the customer has closed the result */
  resultClosed: boolean;
  /** MLS photos to use (if any) */
  mlsPhotos: MlsPhoto[];
  /** Home condition options */
  homeConditionOptions: HomeCondition[];
}

/**
 * MLS photo for a specific home
 * Next ID: 4
 */
export interface MlsPhoto {
  /** example: Kitchen, Main bathroom */
  title: string;
  /** Option for this mls photo */
  proxyImage: ProxyImage[];
  /** example: https://imgdrop.opendoor.com/xxx */
  imageUrl: string;
}

function createBaseHomeConditionResponse(): HomeConditionResponse {
  return {
    homeConditions: [],
    flowDifficulty: "",
    humanId: "",
    submittedAt: undefined,
    skippedAt: undefined,
    hasMlsPhotos: false,
  };
}

export const HomeConditionResponse = {
  fromJSON(object: any): HomeConditionResponse {
    return {
      homeConditions: Array.isArray(object?.homeConditions)
        ? object.homeConditions.map((e: any) => HomeCondition.fromJSON(e))
        : [],
      flowDifficulty: isSet(object.flowDifficulty) ? String(object.flowDifficulty) : "",
      humanId: isSet(object.humanId) ? String(object.humanId) : "",
      submittedAt: isSet(object.submittedAt) ? fromJsonTimestamp(object.submittedAt) : undefined,
      skippedAt: isSet(object.skippedAt) ? fromJsonTimestamp(object.skippedAt) : undefined,
      hasMlsPhotos: isSet(object.hasMlsPhotos) ? Boolean(object.hasMlsPhotos) : false,
    };
  },

  toJSON(message: HomeConditionResponse): unknown {
    const obj: any = {};
    if (message.homeConditions?.length) {
      obj.homeConditions = message.homeConditions.map((e) => HomeCondition.toJSON(e));
    }
    if (message.flowDifficulty !== "") {
      obj.flowDifficulty = message.flowDifficulty;
    }
    if (message.humanId !== "") {
      obj.humanId = message.humanId;
    }
    if (message.submittedAt !== undefined) {
      obj.submittedAt = message.submittedAt.toISOString();
    }
    if (message.skippedAt !== undefined) {
      obj.skippedAt = message.skippedAt.toISOString();
    }
    if (message.hasMlsPhotos === true) {
      obj.hasMlsPhotos = message.hasMlsPhotos;
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<HomeConditionResponse>, I>>(base?: I): HomeConditionResponse {
    return HomeConditionResponse.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<HomeConditionResponse>, I>>(object: I): HomeConditionResponse {
    const message = createBaseHomeConditionResponse();
    message.homeConditions = object.homeConditions?.map((e) => HomeCondition.fromPartial(e)) || [];
    message.flowDifficulty = object.flowDifficulty ?? "";
    message.humanId = object.humanId ?? "";
    message.submittedAt = object.submittedAt ?? undefined;
    message.skippedAt = object.skippedAt ?? undefined;
    message.hasMlsPhotos = object.hasMlsPhotos ?? false;
    return message;
  },
};

function createBaseProxyImage(): ProxyImage {
  return { id: "", title: "", description: "", imageUrl: "", score: 0 };
}

export const ProxyImage = {
  fromJSON(object: any): ProxyImage {
    return {
      id: isSet(object.id) ? String(object.id) : "",
      title: isSet(object.title) ? String(object.title) : "",
      description: isSet(object.description) ? String(object.description) : "",
      imageUrl: isSet(object.imageUrl) ? String(object.imageUrl) : "",
      score: isSet(object.score) ? Number(object.score) : 0,
    };
  },

  toJSON(message: ProxyImage): unknown {
    const obj: any = {};
    if (message.id !== "") {
      obj.id = message.id;
    }
    if (message.title !== "") {
      obj.title = message.title;
    }
    if (message.description !== "") {
      obj.description = message.description;
    }
    if (message.imageUrl !== "") {
      obj.imageUrl = message.imageUrl;
    }
    if (message.score !== 0) {
      obj.score = Math.round(message.score);
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<ProxyImage>, I>>(base?: I): ProxyImage {
    return ProxyImage.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<ProxyImage>, I>>(object: I): ProxyImage {
    const message = createBaseProxyImage();
    message.id = object.id ?? "";
    message.title = object.title ?? "";
    message.description = object.description ?? "";
    message.imageUrl = object.imageUrl ?? "";
    message.score = object.score ?? 0;
    return message;
  },
};

function createBaseHomeCondition(): HomeCondition {
  return { title: "", proxyImages: [], exampleProxyImages: [], selectedProxyImageId: "" };
}

export const HomeCondition = {
  fromJSON(object: any): HomeCondition {
    return {
      title: isSet(object.title) ? String(object.title) : "",
      proxyImages: Array.isArray(object?.proxyImages) ? object.proxyImages.map((e: any) => ProxyImage.fromJSON(e)) : [],
      exampleProxyImages: Array.isArray(object?.exampleProxyImages)
        ? object.exampleProxyImages.map((e: any) => ProxyImage.fromJSON(e))
        : [],
      selectedProxyImageId: isSet(object.selectedProxyImageId) ? String(object.selectedProxyImageId) : "",
    };
  },

  toJSON(message: HomeCondition): unknown {
    const obj: any = {};
    if (message.title !== "") {
      obj.title = message.title;
    }
    if (message.proxyImages?.length) {
      obj.proxyImages = message.proxyImages.map((e) => ProxyImage.toJSON(e));
    }
    if (message.exampleProxyImages?.length) {
      obj.exampleProxyImages = message.exampleProxyImages.map((e) => ProxyImage.toJSON(e));
    }
    if (message.selectedProxyImageId !== "") {
      obj.selectedProxyImageId = message.selectedProxyImageId;
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<HomeCondition>, I>>(base?: I): HomeCondition {
    return HomeCondition.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<HomeCondition>, I>>(object: I): HomeCondition {
    const message = createBaseHomeCondition();
    message.title = object.title ?? "";
    message.proxyImages = object.proxyImages?.map((e) => ProxyImage.fromPartial(e)) || [];
    message.exampleProxyImages = object.exampleProxyImages?.map((e) => ProxyImage.fromPartial(e)) || [];
    message.selectedProxyImageId = object.selectedProxyImageId ?? "";
    return message;
  },
};

function createBasePriceAdjustmentResponse(): PriceAdjustmentResponse {
  return { submittedByOffer: "", bpsIncrease: 0, resultClosed: false, mlsPhotos: [], homeConditionOptions: [] };
}

export const PriceAdjustmentResponse = {
  fromJSON(object: any): PriceAdjustmentResponse {
    return {
      submittedByOffer: isSet(object.submittedByOffer) ? String(object.submittedByOffer) : "",
      bpsIncrease: isSet(object.bpsIncrease) ? Number(object.bpsIncrease) : 0,
      resultClosed: isSet(object.resultClosed) ? Boolean(object.resultClosed) : false,
      mlsPhotos: Array.isArray(object?.mlsPhotos) ? object.mlsPhotos.map((e: any) => MlsPhoto.fromJSON(e)) : [],
      homeConditionOptions: Array.isArray(object?.homeConditionOptions)
        ? object.homeConditionOptions.map((e: any) => HomeCondition.fromJSON(e))
        : [],
    };
  },

  toJSON(message: PriceAdjustmentResponse): unknown {
    const obj: any = {};
    if (message.submittedByOffer !== "") {
      obj.submittedByOffer = message.submittedByOffer;
    }
    if (message.bpsIncrease !== 0) {
      obj.bpsIncrease = Math.round(message.bpsIncrease);
    }
    if (message.resultClosed === true) {
      obj.resultClosed = message.resultClosed;
    }
    if (message.mlsPhotos?.length) {
      obj.mlsPhotos = message.mlsPhotos.map((e) => MlsPhoto.toJSON(e));
    }
    if (message.homeConditionOptions?.length) {
      obj.homeConditionOptions = message.homeConditionOptions.map((e) => HomeCondition.toJSON(e));
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<PriceAdjustmentResponse>, I>>(base?: I): PriceAdjustmentResponse {
    return PriceAdjustmentResponse.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<PriceAdjustmentResponse>, I>>(object: I): PriceAdjustmentResponse {
    const message = createBasePriceAdjustmentResponse();
    message.submittedByOffer = object.submittedByOffer ?? "";
    message.bpsIncrease = object.bpsIncrease ?? 0;
    message.resultClosed = object.resultClosed ?? false;
    message.mlsPhotos = object.mlsPhotos?.map((e) => MlsPhoto.fromPartial(e)) || [];
    message.homeConditionOptions = object.homeConditionOptions?.map((e) => HomeCondition.fromPartial(e)) || [];
    return message;
  },
};

function createBaseMlsPhoto(): MlsPhoto {
  return { title: "", proxyImage: [], imageUrl: "" };
}

export const MlsPhoto = {
  fromJSON(object: any): MlsPhoto {
    return {
      title: isSet(object.title) ? String(object.title) : "",
      proxyImage: Array.isArray(object?.proxyImage) ? object.proxyImage.map((e: any) => ProxyImage.fromJSON(e)) : [],
      imageUrl: isSet(object.imageUrl) ? String(object.imageUrl) : "",
    };
  },

  toJSON(message: MlsPhoto): unknown {
    const obj: any = {};
    if (message.title !== "") {
      obj.title = message.title;
    }
    if (message.proxyImage?.length) {
      obj.proxyImage = message.proxyImage.map((e) => ProxyImage.toJSON(e));
    }
    if (message.imageUrl !== "") {
      obj.imageUrl = message.imageUrl;
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<MlsPhoto>, I>>(base?: I): MlsPhoto {
    return MlsPhoto.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<MlsPhoto>, I>>(object: I): MlsPhoto {
    const message = createBaseMlsPhoto();
    message.title = object.title ?? "";
    message.proxyImage = object.proxyImage?.map((e) => ProxyImage.fromPartial(e)) || [];
    message.imageUrl = object.imageUrl ?? "";
    return message;
  },
};

type Builtin = Date | Function | Uint8Array | string | number | boolean | undefined;

export type DeepPartial<T> = T extends Builtin ? T
  : T extends Array<infer U> ? Array<DeepPartial<U>> : T extends ReadonlyArray<infer U> ? ReadonlyArray<DeepPartial<U>>
  : T extends { $case: string } ? { [K in keyof Omit<T, "$case">]?: DeepPartial<T[K]> } & { $case: T["$case"] }
  : T extends {} ? { [K in keyof T]?: DeepPartial<T[K]> }
  : Partial<T>;

type KeysOfUnion<T> = T extends T ? keyof T : never;
export type Exact<P, I extends P> = P extends Builtin ? P
  : P & { [K in keyof P]: Exact<P[K], I[K]> } & { [K in Exclude<keyof I, KeysOfUnion<P>>]: never };

function fromTimestamp(t: Timestamp): Date {
  let millis = (t.seconds || 0) * 1_000;
  millis += (t.nanos || 0) / 1_000_000;
  return new Date(millis);
}

function fromJsonTimestamp(o: any): Date {
  if (o instanceof Date) {
    return o;
  } else if (typeof o === "string") {
    return new Date(o);
  } else {
    return fromTimestamp(Timestamp.fromJSON(o));
  }
}

function isSet(value: any): boolean {
  return value !== null && value !== undefined;
}
